import { validTitle } from './title-model';

/** Deterministic teaching models. No sockets, files or remote services are used. */
export function ownershipFrame(singleOwner: boolean, step: number) {
  const initial = ['Read notes'];
  const first = step >= 1 ? [...initial, 'Call Alex'] : initial;
  const second = step >= 2 ? [...initial, 'Book room'] : initial;
  const shared = step >= 2 ? [...first, 'Book room'] : first;
  return {
    a: singleOwner ? null : first,
    b: singleOwner ? null : second,
    owner: singleOwner ? shared : null,
    disk: singleOwner ? shared : step >= 2 ? second : first,
    lost: !singleOwner && step >= 2,
  };
}

export function addressFrame(origin: string, address: string, port: string) {
  const destination = address === '127.0.0.1' ? origin : 'workspace';
  // The example server binds only the workspace's loopback interface.
  const connected = destination === 'workspace' && address === '127.0.0.1' && port === '8000';
  const explanation = connected
    ? 'This client and the listener share the workspace network environment. Port 8000 selects the listening socket.'
    : destination === 'laptop'
      ? '127.0.0.1 leads back to the laptop. The workspace server is in a different network environment.'
      : address !== '127.0.0.1'
        ? 'The address reaches the workspace interface, but this listener is bound only to loopback. No listener accepts this destination.'
        : 'The workspace is right, but nothing in this example listens on port 8001.';
  return { destination, connected, explanation };
}

export const connectionActions = [
  'Bind and listen',
  'Connect client',
  'Accept connection',
  'Send request bytes',
  'Receive and reply',
  'Close conversation',
] as const;
export function connectionFrame(step: number) {
  return {
    listener: step >= 1 ? 'Listening on 127.0.0.1:9000' : 'No listening socket yet',
    client: step < 2 ? 'Not connected' : step === 6 ? 'Conversation closed' : 'Connected socket',
    server:
      step < 2
        ? 'No connection yet'
        : step === 2
          ? 'Connection queued; application has not accepted it'
          : step === 6
            ? 'Connected socket closed'
            : 'Separate connected socket returned by accept()',
    request: step >= 4 ? 'Q' : null,
    reply: step >= 5 ? 'A' : null,
    explanation:
      [
        'The server first needs a socket that can receive connection requests.',
        'bind chooses the local address; listen makes this socket a listener. accept would now wait for a connection.',
        'A connection can be established and queued before the server application calls accept. The listener is still available.',
        'accept returns a new socket for this conversation. The original listening socket remains separate.',
        'The client has sent bytes. That alone does not prove the server application has read or processed them.',
        'This first protocol needs exactly one byte: the server reads Q and replies A. A confirms that this conversation was handled. Later we will need framing for longer messages.',
        'Closing the connected sockets ends this conversation. The listening socket remains open for another client.',
      ][step] ?? '',
  };
}

export const streamChunks: Record<string, string[]> = {
  split: ['CRE', 'ATE|Read notes\nLI', 'ST\nPAR'],
  together: ['CREATE|Read notes\nLIST\nPAR'],
  tiny: ['C', 'REATE|', 'Read notes', '\n', 'L', 'IST\n', 'PAR'],
};
export function streamFrame(chunks: string[], received: number, buffered: boolean) {
  const delivered = chunks.slice(0, received);
  if (!buffered) return { messages: delivered, remainder: '', delivered: delivered.join('') };
  const parts = delivered.join('').split('\n');
  return {
    messages: parts.slice(0, -1),
    remainder: parts.at(-1) ?? '',
    delivered: delivered.join(''),
  };
}

export const failureExamples = {
  incomplete: {
    input: 'CREATE|Read',
    result: 'Peer closed before the newline. Reject the incomplete request.',
  },
  silent: {
    input: '(no bytes arrive)',
    result:
      'The receive operation’s inactivity timeout expires. Close this connection so another client can be served.',
  },
  oversized: {
    input: 'CREATE|' + 'x'.repeat(34),
    result: 'The buffer exceeds this model’s 32-byte limit. Reject it before it grows further.',
  },
  malformed: {
    input: 'DANCE\n',
    result: 'A complete line arrived, but DANCE is not a supported command. Send an error reply.',
  },
} as const;
export type FailureExample = keyof typeof failureExamples;

export function privateAgreement(title: string) {
  const wire = `CREATE|${title}`;
  const fields = wire.split('|');
  const json = JSON.stringify({ title });
  const decoded = JSON.parse(json) as { title: string };
  return {
    wire,
    fields,
    valid: fields.length === 2 && validTitle(fields[1] ?? ''),
    json,
    title: decoded.title,
  };
}

export function requestFrame(method: string, path: string, title: string) {
  const body = method === 'POST' ? JSON.stringify({ title }) : '';
  const bodyLength = new TextEncoder().encode(body).length;
  return {
    body,
    bodyLength,
    wire: `${method} ${path} HTTP/1.1\r\nHost: 127.0.0.1:8000\r\nAccept: application/json\r\n${body ? `Content-Type: application/json\r\nContent-Length: ${bodyLength}\r\n` : ''}Connection: close\r\n\r\n${body}`,
  };
}

export const outcomeExamples = {
  list: {
    request: 'GET /tasks',
    code: 200,
    name: 'OK',
    body: '[]',
    change: 'No state change: the collection is empty.',
  },
  create: {
    request: 'POST /tasks · {"title":"Read notes"}',
    code: 201,
    name: 'Created',
    body: '{"id":1,"title":"Read notes","completed":false}',
    change: 'One task saved. next_id advances from 1 to 2.',
  },
  blank: {
    request: 'POST /tasks · {"title":" "}',
    code: 400,
    name: 'Bad Request',
    body: '{"error":"Title cannot be blank"}',
    change: 'No state change. next_id stays 1.',
  },
  route: {
    request: 'GET /unknown',
    code: 404,
    name: 'Not Found',
    body: '{"error":"Unknown route"}',
    change: 'No state change. This route does not exist.',
  },
  method: {
    request: 'DELETE /tasks',
    code: 405,
    name: 'Method Not Allowed',
    body: '{"error":"Method not allowed"}',
    change: 'No state change. The response includes Allow: GET, POST.',
  },
  offline: {
    request: 'GET /tasks · listener stopped',
    code: null,
    name: 'Connection refused',
    body: 'No HTTP response exists.',
    change: 'No request reached an application listener.',
  },
} as const;
export type OutcomeExample = keyof typeof outcomeExamples;

/** HTTP/1.1 teaching subset: known Content-Length, one request per connection. */
export function framingBytes(title: string) {
  const request = requestFrame('POST', '/tasks', title);
  const bytes = new TextEncoder().encode(request.wire);
  const headerEnd = request.wire.indexOf('\r\n\r\n') + 4;
  const unicodeStart = Array.from(bytes).findIndex((byte) => byte >= 128);
  return {
    ...request,
    bytes,
    headerEnd,
    cuts: {
      headers: [headerEnd - 2, headerEnd + 5, bytes.length],
      together: [bytes.length],
      unicode: unicodeStart >= 0 ? [unicodeStart + 1, bytes.length] : [headerEnd + 5, bytes.length],
    },
  };
}
export function framingFrame(title: string, pattern: string, step: number) {
  const source = framingBytes(title);
  const cuts = source.cuts[pattern as keyof typeof source.cuts] ?? source.cuts.headers;
  const received = step === 0 ? 0 : (cuts[Math.min(step, cuts.length) - 1] ?? 0);
  const headerFound = received >= source.headerEnd;
  const bodyReceived = Math.max(0, received - source.headerEnd);
  const complete = headerFound && bodyReceived >= source.bodyLength;
  const bodyBytes = source.bytes.slice(source.headerEnd, received);
  let partialText = '';
  let decodeFailed = false;
  try {
    partialText = new TextDecoder('utf-8', { fatal: true }).decode(bodyBytes);
  } catch {
    decodeFailed = true;
  }
  return {
    ...source,
    cuts,
    received,
    headerFound,
    bodyReceived,
    complete,
    bodyBytes,
    partialText,
    decodeFailed,
  };
}

export const validationExamples = {
  http: { wire: 'POST /tasks (no HTTP version)', body: '{"title":"Read notes"}' },
  json: { wire: 'POST /tasks HTTP/1.1', body: '{"title":' },
  list: { wire: 'POST /tasks HTTP/1.1', body: '["Read notes"]' },
  missing: { wire: 'POST /tasks HTTP/1.1', body: '{}' },
  blank: { wire: 'POST /tasks HTTP/1.1', body: '{"title":"   "}' },
  valid: { wire: 'POST /tasks HTTP/1.1', body: '{"title":"Read notes"}' },
} as const;
export type ValidationExample = keyof typeof validationExamples;
export function validationTrace(example: ValidationExample) {
  const gates = [
    {
      label: 'HTTP structure',
      pass: example !== 'http',
      detail: 'The HTTP request line and message boundaries are valid.',
    },
    { label: 'JSON syntax', pass: example !== 'json', detail: 'Body text can be parsed as JSON.' },
    {
      label: 'JSON object',
      pass: example !== 'list',
      detail: 'The parsed value is an object, not a list.',
    },
    {
      label: 'Title field',
      pass: example !== 'missing',
      detail: 'A title field exists and contains a string.',
    },
    {
      label: 'Task rule',
      pass: example !== 'blank',
      detail: 'The title contains non-whitespace text.',
    },
  ];
  const failures: Record<string, string> = {
    http: 'Reject: the request line is missing a supported HTTP version.',
    json: 'Reject: the JSON text ends before its value is complete.',
    list: 'Reject: valid JSON is not necessarily a JSON object.',
    missing: 'Reject: an object can still be missing the required title.',
    blank: 'Reject: a string of spaces is not a usable task title.',
  };
  const stop = gates.findIndex((gate) => !gate.pass);
  return gates.slice(0, stop < 0 ? gates.length : stop + 1).map((gate) => ({
    ...gate,
    detail: gate.pass ? gate.detail : (failures[example] ?? ''),
  }));
}

export function httpCommitFrame(step: number, saveFails: boolean, replyFails: boolean) {
  const saved = step >= 2 && !saveFails;
  const adopted = step >= 3 && saved;
  const replied = step >= 4;
  return {
    candidate: step >= 1,
    disk: saved,
    accepted: adopted,
    nextId: adopted ? 2 : 1,
    client: !replied
      ? 'Waiting for a response'
      : replyFails
        ? 'No reply received: outcome uncertain'
        : saveFails
          ? '503: change was not accepted'
          : '201: task creation confirmed',
    explanation:
      replied && replyFails
        ? saveFails
          ? 'The save failed and no task was accepted. But the client received no error response, so it cannot know that from its own evidence.'
          : 'The task exists even though the reply was lost. Retrying blindly could create a second task; no automatic retry is modeled.'
        : saveFails && step >= 2
          ? 'The working copy was not saved, so it does not replace current data in memory. Current data in memory, next_id and the previous file stay unchanged.'
          : ([
              'No work has been accepted. Predict when the client can safely be told “Created”.',
              'Make a separate working copy and add the proposed task to it. Current data in memory still has no task.',
              'The working copy has been saved to the file. Current data in memory has not yet been replaced.',
              'Replace current data in memory with the saved working copy. The operation is now accepted, even before the reply reaches the client.',
              'The client receives confirmation only after the server saves the working copy and updates its current data.',
            ][step] ?? ''),
  };
}
